/**
 * Canonical permission constants - Frontend mirror of backend permissions
 *
 * This file MUST mirror backend src/authz/permissions.ts exactly.
 * String values must match backend exactly for contract testing.
 *
 * All permissions follow the naming convention: {scope}:{resource}:{action}
 * See backend docs/permission-naming-convention.md for details
 */

export const Permissions = {
  // Workspace permissions
  WORKSPACE_READ: "workspace:read",
  WORKSPACE_UPDATE: "workspace:update",

  // Workspace member permissions
  WORKSPACE_MEMBER_LIST: "workspace:member:list",
  WORKSPACE_MEMBER_INVITE: "workspace:member:invite",
  WORKSPACE_MEMBER_UPDATE: "workspace:member:update",
  WORKSPACE_MEMBER_REMOVE: "workspace:member:remove",

  // Workspace project permissions
  WORKSPACE_PROJECT_LIST: "workspace:project:list",
  WORKSPACE_PROJECT_CREATE: "workspace:project:create",

  // Workspace billing permissions
  WORKSPACE_BILLING_READ: "workspace:billing:read",
  WORKSPACE_BILLING_UPDATE: "workspace:billing:update",

  // Workspace API key permissions
  WORKSPACE_API_KEY_LIST: "workspace:api_key:list",
  WORKSPACE_API_KEY_MANAGE: "workspace:api_key:manage",

  // Workspace SSO permissions
  WORKSPACE_SSO_MANAGE: "workspace:sso:manage",

  // Project permissions
  PROJECT_READ: "project:read",
  PROJECT_UPDATE: "project:update",
  /** Draft screenplay structure and content (scenes, document, notes, snapshots, revision lock). */
  PROJECT_SCRIPT_UPDATE: "project:script:update",
  /** Freeze the entire script document for production handoff and unfreeze it. */
  PROJECT_SCRIPT_LOCK: "project:script:lock",
  /** Tag definitions, categories, merge/unmerge, and draft scene tag application. */
  PROJECT_TAG_UPDATE: "project:tag:update",
  /** Create, update, revoke, and delete script share links. */
  PROJECT_SHARE_LINK_MANAGE: "project:share_link:manage",
  PROJECT_DELETE: "project:delete",
  PROJECT_ARCHIVE: "project:archive",
  PROJECT_EXPORT: "project:export",

  // Project version permissions
  PROJECT_VERSION_CREATE: "project:version:create",

  // Project budget permissions
  PROJECT_BUDGET_RERUN: "project:budget:rerun",
  /** Lock the budget for finalize/handoff and unlock it again. */
  PROJECT_BUDGET_LOCK: "project:budget:lock",

  // Project schedule permissions
  PROJECT_SCHEDULE_RERUN: "project:schedule:rerun",

  // Comment permissions
  COMMENT_CREATE: "project:comment:create",
  COMMENT_RESOLVE: "project:comment:resolve",
  /** Delete anyone's comment: workspace owner, admin and the project creator. */
  COMMENT_MODERATE: "project:comment:moderate",
} as const;

/**
 * Permission type - Union of all permission string values
 */
export type Permission = (typeof Permissions)[keyof typeof Permissions];

/**
 * Workspace permission type - Extracted using template literal types
 */
export type WorkspacePermission = Extract<Permission, `workspace:${string}`>;

/**
 * Project permission type - Extracted using template literal types
 */
export type ProjectPermission = Extract<Permission, `project:${string}`>;

/**
 * All permissions array - Sorted, readonly
 * Used for validation and contract testing
 */
export const ALL_PERMISSIONS: readonly Permission[] = Object.values(
  Permissions
).sort() as readonly Permission[];

/**
 * All workspace permissions - Filtered and sorted
 */
export const ALL_WORKSPACE_PERMISSIONS: readonly WorkspacePermission[] = (
  Object.values(Permissions).filter((p): p is WorkspacePermission =>
    p.startsWith("workspace:")
  ) as WorkspacePermission[]
).sort();

/**
 * All project permissions - Filtered and sorted
 */
export const ALL_PROJECT_PERMISSIONS: readonly ProjectPermission[] = (
  Object.values(Permissions).filter((p): p is ProjectPermission =>
    p.startsWith("project:")
  ) as ProjectPermission[]
).sort();

/**
 * Scoped permission check helpers
 *
 * These functions provide type-safe permission checking with scope enforcement.
 * TypeScript will prevent calling canWorkspace with project permissions and vice versa.
 */

/**
 * Check if user has a workspace permission
 * @param permission - Workspace permission to check
 * @param userPermissions - Array of permissions the user has
 * @returns true if user has the permission
 */
export function canWorkspace(permission: WorkspacePermission, userPermissions: string[]): boolean {
  return userPermissions.includes(permission);
}

/**
 * Check if user has a project permission
 * @param permission - Project permission to check
 * @param userPermissions - Array of permissions the user has
 * @returns true if user has the permission
 */
export function canProject(permission: ProjectPermission, userPermissions: string[]): boolean {
  return userPermissions.includes(permission);
}
